import { useTranslation } from 'react-i18next';

import { ArrowDownRight, ArrowUpRight, MoveRight } from 'lucide-react';

import { BreathLineChart } from './charts';
import { ItemRow } from './ResultRow';
import { Card, Pill, SectionHeadCard } from '../../../../components';
import { breathRows } from '../../breathRows';
import { scoreLevel } from '../../resultCopy';

import type { PillTone } from '../../../../components';
import type { DiagnosisRecord } from '../../record';
import type { QualitativeLevel } from '../../resultCopy';

/**
 * 호흡 안정도 — 지속음의 시작·중간·후반 흔들림(시안 §8.7 `1000:8628`): 범례 카드 · 라인
 * 차트 · 세 칸 점수 · `구간별 안정도` 행.
 *
 * 종합 점수 한 줄(`소리 안정성`, 진단 1)과 **겹치지 않는다**: 저건 테이크 전체의 흔들림이고
 * 여기는 그 흔들림이 언제 있었는지다. 한 숫자로는 처음에 흔들리다 잡힌 사람과 잘 시작했다가
 * 숨이 떨어진 사람이 똑같이 보이는데, 두 사람에게 할 말은 정반대다.
 *
 * **구간 경계는 그 회차가 소유한다**(`breathRows`). 경계는 테이크 길이의 비율이라
 * 회차마다 다르다 — 6초 테이크는 `0~1.8초`에서 시작한다. 화면이 `0~3초`를 자기 문자열에
 * 박으면 대부분의 회차에 대해 틀린 말을 한다.
 *
 * 시안의 `평균 기준선`은 비교할 평균이 없어 그리지 않는다.
 */

const TONE: Record<QualitativeLevel, PillTone> = {
  high: 'pink',
  mid: 'mint',
  low: 'orange',
};

const ICON: Record<QualitativeLevel, typeof ArrowUpRight> = {
  high: ArrowUpRight,
  mid: MoveRight,
  low: ArrowDownRight,
};

export const BreathSection = ({ record }: { record: DiagnosisRecord }) => {
  const { t } = useTranslation();
  const rows = breathRows(record.breath ?? []);
  const scored = rows.flatMap((row) =>
    row.score === null ? [] : [{ id: row.id, score: row.score }],
  );

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-8 py-4">
        <Card className="mx-[18px] flex items-center gap-4 px-5 py-[18px] text-sm font-medium text-bk-800">
          <span className="flex items-center gap-2">
            <span
              aria-hidden="true"
              className="h-[3px] w-4 rounded-sm bg-primary"
            />
            {t('web.result.breath.legendScore')}
          </span>
          {/* 평균 선은 차트에 안 넘긴다(`BreathLineChart`의 `average` 미사용) —
              범례만 남기면 없는 점선을 약속한다. */}
        </Card>

        {/* 못 잰 회차에서는 차트가 스스로 안 그려진다. */}
        {scored.length === 0 ? (
          <p className="text-center text-sm font-semibold text-bk-600">
            {t('web.result.pending')}
          </p>
        ) : (
          <BreathLineChart
            segments={rows}
            scores={scored}
            series={record.pitchSeries}
          />
        )}

        <div className="mx-[18px] grid grid-cols-3 gap-2 text-center">
          {rows.map(({ id, fromSec, toSec, score }) => {
            return (
              <div key={id} className="flex flex-col items-center gap-1">
                <span className="text-sm font-semibold text-bk-900">
                  {t(`web.result.breath.name.${id}`)}
                </span>
                {fromSec !== null && toSec !== null && (
                  <span className="text-xs text-bk-600">
                    {t('web.result.breath.axisSec', { value: fromSec })} ~{' '}
                    {t('web.result.breath.axisSec', { value: toSec })}
                  </span>
                )}
                <span
                  className={
                    score == null
                      ? 'mt-1 text-xs font-medium text-bk-600'
                      : scoreLevel(score) === 'low'
                        ? 'mt-1 text-xl font-bold tabular-nums text-orange'
                        : 'mt-1 text-xl font-bold tabular-nums text-primary'
                  }
                >
                  {score == null
                    ? t('web.result.pending')
                    : t('web.result.breath.point', { value: score })}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex flex-col gap-8 py-4">
        <SectionHeadCard
          size="md"
          className="mx-[18px]"
          title={t('web.result.breath.bySegment')}
        />
        <div className="flex flex-col gap-2">
          {rows.map(({ id, fromSec, toSec, score }) => {
            // 점수 없는 구간 = 표본이 모자랐다. 줄은 남기되 값만 빈 문구다 — 줄까지
            // 지우면 세 구간 중 무엇이 빠졌는지 화면에서 사라진다.
            const level = score == null ? null : scoreLevel(score);
            const Icon = level ? ICON[level] : MoveRight;
            return (
              <ItemRow
                key={id}
                icon={
                  <Icon
                    className={
                      level === 'low'
                        ? 'h-5 w-5 text-orange'
                        : level
                          ? 'h-5 w-5 text-primary'
                          : 'h-5 w-5 text-bk-400'
                    }
                    aria-hidden="true"
                  />
                }
                // 초를 모르는 회차(서버 복원·비율 이전)는 구간 이름만 — `breathRows` 주석.
                title={
                  fromSec === null || toSec === null
                    ? t(`web.result.breath.name.${id}`)
                    : t('web.result.breath.row', {
                        name: t(`web.result.breath.name.${id}`),
                        from: fromSec,
                        to: toSec,
                      })
                }
                desc={level ? t(`web.result.breath.desc.${level}`) : undefined}
                right={
                  <span className="flex shrink-0 items-center gap-2">
                    {level && score != null && (
                      <span
                        className={
                          level === 'low'
                            ? 'text-base font-bold tabular-nums text-orange'
                            : 'text-base font-bold tabular-nums text-primary'
                        }
                      >
                        {t('web.result.breath.point', { value: score })}
                      </span>
                    )}
                    <Pill tone={level ? TONE[level] : 'gray'}>
                      {level
                        ? t(`web.result.breath.badge.${level}`)
                        : t('web.result.pending')}
                    </Pill>
                  </span>
                }
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};
